/* ============================================================
   Shoppable UGC widget behaviour
   Custom element <nv-shoppable-ugc> — auto-inits on insert,
   so it also survives Shopify theme-editor re-renders.
   ============================================================ */
(function () {
  'use strict';

  var ICON = {
    close: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>'
  };

  var MUTED_SVG = '<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4v8a4.5 4.5 0 002.5-4z" opacity=".0"/><path d="M4 9v6h4l5 5V4L8 9H4zm12 8.5l-2-2-2 2-1.4-1.4 2-2-2-2L12 8.6l2 2 2-2L17.4 10l-2 2 2 2z"/></svg>';
  var UNMUTED_SVG = '<svg viewBox="0 0 24 24"><path d="M4 9v6h4l5 5V4L8 9H4zm11.5 3a4.5 4.5 0 00-2.5-4v8a4.5 4.5 0 002.5-4zM13 3.2v2.1a7 7 0 010 13.4v2.1a9 9 0 000-17.6z"/></svg>';

  // Registry of widgets that are hidden-by-default (A/B mode), so an A/B test
  // can reveal them. The variant just calls window.NvUgcEnable(). Works whether
  // it runs before or after this script (the flag is checked on connect too).
  var instances = [];
  window.NvUgcEnable = function () {
    window.NV_UGC_ENABLED = true;
    instances.forEach(function (i) { i.enable(); });
  };

  class NvShoppableUgc extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.lightbox = this.querySelector('[data-ugc-lightbox]');
      this.track = this.querySelector('[data-ugc-track]');
      this.stage = this.querySelector('[data-ugc-stage]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-ugc-slide]'));
      this.current = 0;
      this.muted = false; // sound on by default; user can mute
      this.isOpen = false;

      if (!this.lightbox || !this.slides.length) return;

      // Product card carousel state.
      this.products = this.parseProducts();
      this.currentProductId = parseInt(this.getAttribute('data-ugc-current-id'), 10) || null;
      this.productIndex = 0;
      this.rotTimer = null;
      this.cards = Array.prototype.slice.call(this.lightbox.querySelectorAll('[data-ugc-card]'));

      // A/B gating: when hidden-by-default, stay dormant (no init, no thumbnail
      // or video loads) until window.NvUgcEnable() is called by the variant.
      this.abHidden = this.classList.contains('nv-ugc-ab-hidden');
      instances.push(this);
      if (!this.abHidden || window.NV_UGC_ENABLED) {
        this.enable();
      }
    }

    // Reveal + initialise the widget. Idempotent.
    enable() {
      if (this._enabled) return;
      this._enabled = true;
      this.classList.remove('nv-ugc-ab-hidden');
      // Load lazy thumbnails (present only in A/B-hidden mode).
      this.querySelectorAll('.nv-ugc-thumb img[data-src]').forEach(function (img) {
        img.src = img.getAttribute('data-src');
        img.removeAttribute('data-src');
      });
      this.buildIcons();
      this.bind();
      this.portal();
      this.renderCards(false);
    }

    parseProducts() {
      var el = this.querySelector('[data-ugc-products]');
      if (!el) return [];
      try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
    }

    disconnectedCallback() {
      var i = instances.indexOf(this);
      if (i > -1) instances.splice(i, 1);
      // Remove the portaled lightbox when this widget is torn down
      // (e.g. Shopify theme-editor re-render) to avoid orphans.
      if (this.lightbox && this.lightbox.parentNode === document.body) {
        this.lightbox.remove();
      }
    }

    // Move the lightbox to <body> so position:fixed is relative to the
    // viewport, not a transformed/sticky ancestor (product info column).
    portal() {
      var id = this.lightbox.getAttribute('data-ugc-portal');
      var self = this;
      if (id) {
        document.querySelectorAll('body > [data-ugc-portal="' + id + '"]').forEach(function (n) {
          if (n !== self.lightbox) n.remove();
        });
      }
      document.body.appendChild(this.lightbox);
    }

    buildIcons() {
      var closeBtn = this.querySelector('[data-ugc-close]');
      var prevBtn = this.querySelector('[data-ugc-prev]');
      var nextBtn = this.querySelector('[data-ugc-next]');
      if (closeBtn) closeBtn.innerHTML = ICON.close;
      if (prevBtn) prevBtn.innerHTML = ICON.prev;
      if (nextBtn) nextBtn.innerHTML = ICON.next;
    }

    bind() {
      var self = this;

      // Open from inline thumbnails
      this.querySelectorAll('[data-ugc-open]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.open(parseInt(el.getAttribute('data-ugc-open'), 10) || 0);
        });
      });

      this.querySelector('[data-ugc-close]').addEventListener('click', function () { self.close(); });
      var prev = this.querySelector('[data-ugc-prev]');
      var next = this.querySelector('[data-ugc-next]');
      if (prev) prev.addEventListener('click', function () { self.go(self.current - 1); });
      if (next) next.addEventListener('click', function () { self.go(self.current + 1); });

      // Backdrop click closes (only when the stage background, not a control, is hit)
      this.lightbox.addEventListener('click', function (e) {
        if (e.target === self.lightbox || e.target === self.stage) self.close();
      });

      // Per-slide controls
      this.slides.forEach(function (slide, i) {
        var video = slide.querySelector('[data-ugc-video]');
        var mute = slide.querySelector('[data-ugc-mute]');
        var add = slide.querySelector('[data-ugc-add]');

        if (mute) mute.addEventListener('click', function () { self.toggleMute(); });
        if (add) add.addEventListener('click', function () { self.addToCart(add); });

        var playpause = slide.querySelector('[data-ugc-playpause]');

        if (video) {
          video.addEventListener('timeupdate', function () {
            if (i === self.current) self.updateProgress(slide, video);
          });
          video.addEventListener('ended', function () {
            if (i === self.current) self.go(self.current + 1);
          });
          // Reflect play/pause state so the paused overlay icon can show.
          video.addEventListener('play', function () { slide.classList.remove('is-paused'); });
          video.addEventListener('pause', function () { slide.classList.add('is-paused'); });
          // Tapping the active video toggles play/pause.
          video.addEventListener('click', function () {
            if (i !== self.current) return;
            if (video.paused) video.play(); else video.pause();
          });
        }

        if (playpause) {
          playpause.addEventListener('click', function (e) {
            e.stopPropagation();
            if (video) video.play();
          });
        }
      });

      // Hold the rotation while the shopper is engaging with a card, so the
      // product they are looking at is still the product they add.
      this.cards.forEach(function (card) {
        ['pointerenter', 'focusin', 'touchstart'].forEach(function (ev) {
          card.addEventListener(ev, function () { self.stopRotation(); }, { passive: true });
        });
        ['pointerleave', 'focusout'].forEach(function (ev) {
          card.addEventListener(ev, function () {
            if (self.isOpen && !self._adding) self.startRotation();
          });
        });
      });

      // Keyboard
      this._onKey = function (e) {
        if (!self.isOpen) return;
        if (e.key === 'Escape') self.close();
        else if (e.key === 'ArrowLeft') self.go(self.current - 1);
        else if (e.key === 'ArrowRight') self.go(self.current + 1);
      };
      document.addEventListener('keydown', this._onKey);

      // Touch swipe
      var startX = 0, startY = 0, tracking = false;
      this.stage.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX; startY = e.touches[0].clientY; tracking = true;
      }, { passive: true });
      this.stage.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          self.go(dx < 0 ? self.current + 1 : self.current - 1);
        }
      }, { passive: true });

      window.addEventListener('resize', function () { if (self.isOpen) self.center(false); });
    }

    open(index) {
      this.lightbox.hidden = false;
      // force reflow so the transition runs
      void this.lightbox.offsetWidth;
      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      this.go(index, true);
      this.startRotation();
    }

    close() {
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.isOpen = false;
      this.stopRotation();
      this.slides.forEach(function (s) {
        var v = s.querySelector('[data-ugc-video]');
        if (v) { v.pause(); }
      });
      var self = this;
      setTimeout(function () { if (!self.isOpen) self.lightbox.hidden = true; }, 220);
    }

    // Rotate the product card through the selected products, one every 5s.
    startRotation() {
      this.stopRotation();
      if (this.products.length <= 1) return;
      var self = this;
      this.rotTimer = setInterval(function () {
        self.productIndex = (self.productIndex + 1) % self.products.length;
        self.renderCards(true);
      }, 5000);
    }

    stopRotation() {
      if (this.rotTimer) { clearInterval(this.rotTimer); this.rotTimer = null; }
    }

    // Fill one card's content from a product object.
    fillCard(inner, prod) {
      // Bind the product to the card here, where the visible content changes,
      // so the two can never disagree. Reading the rotation index at click time
      // could hand back the next product while the old one was still painted.
      inner.__nvProd = prod;
      var link = inner.querySelector('[data-ugc-card-link]');
      var img = inner.querySelector('[data-ugc-card-img]');
      var title = inner.querySelector('[data-ugc-card-title]');
      var price = inner.querySelector('[data-ugc-card-price]');
      var add = inner.querySelector('[data-ugc-add]');
      if (link) link.href = prod.url;
      if (img) {
        if (prod.img) { img.src = prod.img; img.hidden = false; img.alt = prod.title || ''; }
        else { img.hidden = true; }
      }
      if (title) title.textContent = prod.title || '';
      if (price) price.textContent = prod.price || '';
      if (add) {
        add.disabled = !prod.available;
        add.textContent = prod.available ? 'Add to cart' : 'Sold out';
      }
    }

    // Paint every card with the current product. When `animate`, the old card
    // slides out to the left and the new one slides in from the right, so the
    // change reads as a carousel rather than a flicker.
    renderCards(animate) {
      var prod = this.products[this.productIndex];
      if (!prod) return;
      var self = this;
      this.cards.forEach(function (card) {
        var inner = card.querySelector('[data-ugc-card-inner]');
        if (!inner) return;
        if (animate && inner.animate) {
          inner.animate(
            [{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(-100%)', opacity: 0 }],
            { duration: 220, easing: 'ease-in' }
          ).onfinish = function () {
            self.fillCard(inner, prod);
            inner.animate(
              [{ transform: 'translateX(100%)', opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }],
              { duration: 260, easing: 'ease-out' }
            );
          };
        } else {
          self.fillCard(inner, prod);
        }
      });
    }

    go(index, instant) {
      var total = this.slides.length;
      index = (index + total) % total; // wrap
      this.current = index;

      this.slides.forEach(function (slide, i) {
        var video = slide.querySelector('[data-ugc-video]');
        slide.classList.toggle('is-active', i === index);
        if (!video) return;
        if (i === index) {
          this.playActive(video);
        } else {
          video.pause();
          video.currentTime = 0;
          // Only the active video and its immediate neighbours preload, so we
          // don't try to buffer every clip at once (keeps things smooth).
          if (Math.abs(i - index) <= 1) {
            if (video.preload !== 'auto') { video.preload = 'auto'; video.load(); }
          } else {
            video.preload = 'none';
          }
        }
      }, this);

      this.resetProgress(index);
      this.center(instant);
      this.syncMuteIcon();
    }

    // Play the active video with sound; if the browser blocks unmuted
    // playback, fall back to muted so it always plays.
    playActive(video) {
      var self = this;
      video.preload = 'auto';
      video.muted = this.muted;
      var p = video.play();
      if (p && p.catch) {
        p.catch(function () {
          if (!video.muted) {
            video.muted = true;
            self.muted = true;
            self.syncMuteIcon();
            var p2 = video.play();
            if (p2 && p2.catch) p2.catch(function () {});
          }
        });
      }
    }

    center(instant) {
      var slide = this.slides[this.current];
      if (!slide) return;
      var offset = this.stage.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
      if (instant) {
        var prevT = this.track.style.transition;
        this.track.style.transition = 'none';
        this.track.style.transform = 'translateX(' + offset + 'px)';
        void this.track.offsetWidth;
        this.track.style.transition = prevT;
      } else {
        this.track.style.transform = 'translateX(' + offset + 'px)';
      }
    }

    resetProgress(index) {
      this.slides.forEach(function (slide, i) {
        var segs = slide.querySelectorAll('.nv-ugc-progress .seg');
        segs.forEach(function (seg, si) {
          var fill = seg.querySelector('i');
          seg.classList.toggle('is-done', si < index);
          if (fill && si !== index) fill.style.width = si < index ? '100%' : '0';
          if (fill && si === index) fill.style.width = '0';
        });
      });
    }

    updateProgress(slide, video) {
      if (!video.duration) return;
      var segs = slide.querySelectorAll('.nv-ugc-progress .seg');
      var seg = segs[this.current];
      if (seg) {
        var fill = seg.querySelector('i');
        if (fill) fill.style.width = (video.currentTime / video.duration * 100) + '%';
      }
    }

    toggleMute() {
      this.muted = !this.muted;
      var slide = this.slides[this.current];
      var video = slide && slide.querySelector('[data-ugc-video]');
      if (video) video.muted = this.muted;
      this.syncMuteIcon();
    }

    syncMuteIcon() {
      var self = this;
      this.slides.forEach(function (slide) {
        var btn = slide.querySelector('[data-ugc-mute]');
        if (btn) {
          btn.innerHTML = self.muted ? MUTED_SVG : UNMUTED_SVG;
          btn.setAttribute('aria-label', self.muted ? 'Unmute' : 'Mute');
        }
      });
    }

    // The product shown on the card that was clicked. Bound in fillCard, so it
    // is always whatever the shopper could actually see.
    cardProduct(btn) {
      var inner = btn.closest('[data-ugc-card-inner]');
      return (inner && inner.__nvProd) || this.products[this.productIndex] || null;
    }

    setBusy(btn, busy) {
      if (!btn) return;
      btn.classList.toggle('is-loading', !!busy);
      btn.disabled = !!busy;
    }

    // Add straight to the cart via the Ajax API.
    //
    // We deliberately do NOT route through the page's own product form. The
    // store's pre-purchase offer app (Zipify OCU) intercepts that click at
    // document level, cancels it, and rebuilds the request from the page's own
    // product context — which added the wrong item and cost seconds waiting on
    // its popup. Posting the variant ourselves is immediate and unambiguous.
    // Trade-off: no pre-purchase offer popup on widget adds. Post-purchase
    // upsells at checkout are unaffected.
    addToCart(btn) {
      if (this._adding) return;

      var prod = this.cardProduct(btn);
      if (!prod || !prod.available || !prod.variantId) return;

      var self = this;
      this._adding = true;
      this.stopRotation();
      this.setBusy(btn, true);

      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';

      // Single-item form, not the `items: []` batch form: the theme's cart
      // notification keys off `key` in the response, and only this shape
      // returns it. With the batch form it renders nothing and throws.
      var body = { id: prod.variantId, quantity: 1 };
      // Asking for the cart sections lets the theme's notification/drawer
      // re-render from this one response, with no extra round trip.
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }

      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          self.setBusy(btn, false);
          self._adding = false;
          if (res.status) return; // Shopify error object (sold out, bad variant)
          self.close();
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(res);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function () {
          self.setBusy(btn, false);
          self._adding = false;
        });
    }
  }

  if (!customElements.get('nv-shoppable-ugc')) {
    customElements.define('nv-shoppable-ugc', NvShoppableUgc);
  }
})();
